// components/ProductModal.jsx
import React, { useState, useEffect } from 'react';

const ProductModal = ({ 
  selectedItem, 
  videoRefs, 
  getRutubeId, 
  onClose 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  
  const images = React.useMemo(() => {
    if (selectedItem?.images && selectedItem.images.length > 0) {
      return selectedItem.images.filter(img => img && img.trim() !== '');
    } else if (selectedItem?.image && selectedItem.image.trim() !== '') {
      return [selectedItem.image];
    }
    return [];
  }, [selectedItem]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedItem?.id]);

  const nextImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? images.length - 1 : prevIndex - 1
      );
    }
  };

  const goToImage = (index) => {
    if (index >= 0 && index < images.length) {
      setCurrentImageIndex(index);
    }
  };

  const openFullscreen = (imageUrl) => {
    setFullscreenImage(imageUrl);
  };

  const closeFullscreen = () => {
    setFullscreenImage(null);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (fullscreenImage) {
        if (e.key === 'Escape') {
          closeFullscreen();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [images.length, fullscreenImage]);

  const getVideoType = (url) => {
    if (!url) return null;
    
    if (url.includes('rutube.ru') || url.includes('rutube')) {
      return 'rutube';
    } else if (url.match(/\.(mp4|webm|mov|avi|m4v)$/i)) {
      return 'direct';
    }
    return 'direct';
  };

  const videoType = getVideoType(selectedItem.videoUrl);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !fullscreenImage) {
      onClose();
    }
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const renderQualityBadge = () => {
    if (!selectedItem.quality) return null;
    
    const qualityColors = {
      'Premium': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      'Luxury': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      'Exclusive': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      'Professional': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
    };
    
    const qualityClass = qualityColors[selectedItem.quality] || 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    
    return (
      <div className={`border text-xs font-medium ${qualityClass}`}>
      </div>
    );
  };

  const renderColorWithName = (color, index) => {
    const colorNames = {
      '#000000': 'Черный',
      '#FFFFFF': 'Белый',
      '#FF0000': 'Красный',
      '#00FF00': 'Зеленый',
      '#0000FF': 'Синий',
      '#FFFF00': 'Желтый',
      '#FFA500': 'Оранжевый',
      '#800080': 'Фиолетовый',
      '#808080': 'Серый',
      '#964B00': 'Коричневый',
      '#FFC0CB': 'Розовый',
      '#A52A2A': 'Коричневый',
      '#000080': 'Темно-синий',
      '#008000': 'Темно-зеленый'
    };
    
    const colorName = colorNames[color.toUpperCase()] || `Цвет ${index + 1}`;
    
    return (
      <div key={index} className="text-center group relative">
        <div 
          className="w-14 h-14 md:w-16 md:h-16 rounded-xl mb-2 border-3 border-gray-700 shadow-lg transition-transform duration-200 group-hover:scale-110 cursor-pointer"
          style={{ 
            backgroundColor: color || '#000000',
            borderColor: color === '#FFFFFF' ? '#374151' : 'transparent'
          }}
          title={colorName}
        />
        <div className="text-xs text-gray-400 truncate" title={colorName}>
          {colorName}
        </div>
        <div className="text-xs text-gray-500 font-mono hidden group-hover:block absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full bg-gray-800 px-2 py-1 rounded whitespace-nowrap z-10">
          {color}
        </div>
      </div>
    );
  };

  if (fullscreenImage) {
    return (
      <div 
        className="fixed inset-0 z-[60] bg-black flex items-center justify-center"
        onClick={closeFullscreen}
      >
        <button 
          onClick={closeFullscreen}
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-gray-800/80 hover:bg-gray-800 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200"
          aria-label="Закрыть полноэкранный режим"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="relative max-w-5xl max-h-[90vh] p-4" onClick={stopPropagation}>
          <img 
            src={fullscreenImage} 
            alt="Полноэкранное изображение"
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
          />
          
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                  setFullscreenImage(images[currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1]);
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-gray-800/80 hover:bg-gray-800 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 z-10"
                aria-label="Предыдущее изображение"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                  setFullscreenImage(images[currentImageIndex === images.length - 1 ? 0 : currentImageIndex + 1]);
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-gray-800/80 hover:bg-gray-800 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 z-10"
                aria-label="Следующее изображение"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-2 md:p-4 animate-fadeIn overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-black/95" onClick={onClose}></div>
      
      <div 
        className="relative w-full max-w-2xl my-4 md:my-0"
        onClick={stopPropagation}
      >
        <button 
          onClick={onClose}
          className="absolute -top-3 -right-3 z-50 w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform duration-200 md:w-12 md:h-12 md:-top-4 md:-right-4"
          aria-label="Закрыть"
        >
          <svg 
            className="w-5 h-5 md:w-6 md:h-6 text-black" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={3} 
              d="M6 18L18 6M6 6l12 12" 
            />
          </svg>
        </button>
        
        <div className="relative w-full max-h-[calc(100vh-2rem)] md:max-h-[90vh] overflow-y-auto rounded-2xl bg-gradient-to-br from-gray-900 via-black to-gray-900">
          <div className="p-4 md:p-6">
            <div className="mb-4 md:mb-5">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                <h2 className="text-xl md:text-2xl font-bold text-white pr-8">
                  {selectedItem.name}
                </h2>
                {renderQualityBadge()}
              </div>
              
              {selectedItem.categoryName && (
                <div className="flex items-center text-sm text-gray-400">
                  <svg className="w-4 h-4 mr-2 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
                  </svg>
                  {selectedItem.categoryName}
                </div>
              )}
            </div>

            <div className="relative mb-4 md:mb-6 overflow-hidden rounded-xl md:rounded-2xl bg-gray-900">
              {images.length > 0 ? (
                <>
                  <div className="relative">
                    <img 
                      src={images[currentImageIndex]} 
                      alt={`${selectedItem.name} - изображение ${currentImageIndex + 1}`}
                      className="w-full h-52 md:h-64 object-cover transition-opacity duration-300 cursor-zoom-in"
                      onClick={() => openFullscreen(images[currentImageIndex])}
                    />
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none"></div>
                    
                    <button
                      onClick={() => openFullscreen(images[currentImageIndex])}
                      className="absolute top-3 right-3 w-10 h-10 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 z-10"
                      aria-label="Открыть полноэкранный просмотр"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-5h4m0 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
                      </svg>
                    </button>
                  </div>
                  
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 z-10"
                        aria-label="Предыдущее изображение"
                      >
                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 z-10"
                        aria-label="Следующее изображение"
                      >
                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                        {images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => goToImage(index)}
                            className={`w-2 h-2 rounded-full transition-all duration-200 ${
                              index === currentImageIndex 
                                ? 'bg-amber-500 w-6' 
                                : 'bg-white/60 hover:bg-white/80'
                            }`}
                            aria-label={`Перейти к изображению ${index + 1}`}
                          />
                        ))}
                      </div>
                      
                      <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded-lg z-10 backdrop-blur-sm">
                        {currentImageIndex + 1} / {images.length}
                      </div>
                    </>
                  )}
                  
                  {images.length > 1 && (
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent flex space-x-2 z-10 overflow-x-auto">
                      {images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => goToImage(index)}
                          className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 flex-shrink-0 ${
                            index === currentImageIndex 
                              ? 'border-amber-500 ring-2 ring-amber-500/30' 
                              : 'border-transparent hover:border-white/50'
                          }`}
                          aria-label={`Показать изображение ${index + 1}`}
                        >
                          <img 
                            src={image} 
                            alt={`Миниатюра ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-52 md:h-64 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-400 text-sm">Изображение недоступно</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-4 md:mb-6">
              <h3 className="text-sm font-semibold text-gray-300 mb-2 flex items-center">
                <svg className="w-4 h-4 mr-2 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                </svg>
                Описание
              </h3>
              <div className="bg-gray-800/30 p-4 rounded-lg">
                <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                  {selectedItem.description}
                </p>
              </div>
            </div>

            {(selectedItem.features || []).length > 0 && (
              <div className="mb-4 md:mb-6">
                <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
                  </svg>
                  Особенности
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(selectedItem.features || []).filter(f => f && f.trim() !== '').map((feature, index) => (
                    <div 
                      key={index} 
                      className="bg-gray-800/40 p-3 rounded-lg border border-gray-700/50 hover:border-gray-600 transition-colors"
                    >
                      <div className="flex items-start">
                        <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-1.5 mr-3 flex-shrink-0"></div>
                        <span className="text-sm leading-relaxed break-words hyphens-auto">
                          {feature}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedItem.specifications && Object.values(selectedItem.specifications).some(v => v) && (
              <div className="mb-4 md:mb-6">
                <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                  Спецификации
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { key: 'material', label: 'Материал', value: selectedItem.specifications.material },
                    { key: 'threadCount', label: 'Плотность', value: selectedItem.specifications.threadCount }
                  ].map((spec) => (
                    spec.value && (
                      <div key={spec.key} className="bg-gray-800/40 p-4 rounded-lg border border-gray-700/50 text-center">
                        <div className="text-xs text-gray-400 mb-1 uppercase tracking-wider">
                          {spec.label}
                        </div>
                        <div className="font-semibold text-sm truncate" title={spec.value}>
                          {spec.value}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}


            {selectedItem.videoUrl && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-300 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/>
                    </svg>
                    {videoType === 'rutube' ? 'Rutube видео' : 'Видео'}
                  </h3>
                  {selectedItem.videoDuration && (
                    <span className="text-xs text-gray-400 bg-gray-800/50 px-2 py-1 rounded">
                      {selectedItem.videoDuration}
                    </span>
                  )}
                </div>
                
                {videoType === 'rutube' ? (
                  <div className="relative overflow-hidden rounded-lg aspect-video bg-black">
                    <iframe
                      src={`https://rutube.ru/play/embed/${getRutubeId(selectedItem.videoUrl)}?rel=0&modestbranding=1&controls=1&autoplay=0`}
                      className="absolute inset-0 w-full h-full"
                      title="Rutube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                ) : (
                  <div className="relative overflow-hidden rounded-lg bg-black">
                    <video
                      ref={el => videoRefs.current[`modal-${selectedItem.id}`] = el}
                      src={selectedItem.videoUrl}
                      className="w-full h-48 md:h-56 object-cover"
                      playsInline
                      controls
                      poster={images[0]}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              {selectedItem.videoUrl && videoType === 'rutube' && (
                <a
                  href={selectedItem.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-3 bg-gradient-to-r from-purple-600/20 to-pink-600/20 hover:from-purple-600/30 hover:to-pink-600/30 text-purple-300 font-medium rounded-xl text-center border border-purple-700/50 hover:border-purple-600 transition-all duration-200 flex items-center justify-center group"
                >
                  <svg className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/>
                  </svg>
                  Смотреть на Rutube
                </a>
              )}
              
              {selectedItem.link && selectedItem.link !== '#' && (
                <a
                  href={selectedItem.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-3 bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-700 hover:to-orange-600 text-white font-medium rounded-xl text-center transition-all duration-200 flex items-center justify-center group shadow-lg"
                >
                  <svg className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                  </svg>
                  Перейти к товару
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;